import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useCryptoStore } from '../../stores/cryptoStore';
import { bootstrapNewChannelKey } from '../../lib/newChannelKey';

interface OrgMemberRow {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
}

interface Props {
  orgId: string;
  orgSlug: string;
  open: boolean;
  onClose: () => void;
}

function filterMembers(
  members: OrgMemberRow[],
  currentUserId: string | undefined,
  q: string,
): OrgMemberRow[] {
  const needle = q.trim().toLowerCase();
  return members.filter((m) => {
    if (m.id === currentUserId) return false;
    if (!needle) return true;
    const hay = `${m.displayName} ${m.email}`.toLowerCase();
    return hay.includes(needle);
  });
}

export default function NewChatModal({ orgId, orgSlug, open, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const [tab, setTab] = useState<'dm' | 'group'>('dm');
  const [dmQuery, setDmQuery] = useState('');
  const [dmDebounced, setDmDebounced] = useState('');
  const [grQuery, setGrQuery] = useState('');
  const [grDebounced, setGrDebounced] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => setDmDebounced(dmQuery), 200);
    return () => clearTimeout(t);
  }, [dmQuery]);

  useEffect(() => {
    const t = setTimeout(() => setGrDebounced(grQuery), 200);
    return () => clearTimeout(t);
  }, [grQuery]);

  useEffect(() => {
    if (!open) {
      setTab('dm');
      setDmQuery('');
      setDmDebounced('');
      setGrQuery('');
      setGrDebounced('');
      setGroupName('');
      setSelectedIds(new Set());
    }
  }, [open]);

  const {
    data: members = [],
    isLoading: membersLoading,
    isError: membersError,
  } = useQuery<OrgMemberRow[]>({
    queryKey: ['orgMembers', orgId],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/orgs/${orgId}/members`);
      return data.data;
    },
    enabled: open && !!orgId,
  });

  const dmRows = useMemo(
    () => filterMembers(members, currentUserId, dmDebounced),
    [members, currentUserId, dmDebounced],
  );

  const grRows = useMemo(
    () => filterMembers(members, currentUserId, grDebounced),
    [members, currentUserId, grDebounced],
  );

  const dmMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { data } = await api.post(`/api/v1/orgs/${orgId}/dm`, { targetUserId });
      return data.data as { id: string };
    },
    onSuccess: (ch) => {
      queryClient.invalidateQueries({ queryKey: ['channels', orgId] });
      onClose();
      navigate(`/app/${orgSlug}/${ch.id}`);
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      toast.error(axiosErr.response?.data?.error?.message || 'Could not start direct message');
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: async () => {
      const name = groupName.trim();
      const { data } = await api.post(`/api/v1/orgs/${orgId}/channels`, { name, type: 'PRIVATE' });
      const channel = data.data as { id: string };
      let addFailed = 0;
      for (const uid of selectedIds) {
        try {
          await api.post(`/api/v1/channels/${channel.id}/members`, { userId: uid });
        } catch {
          addFailed += 1;
        }
      }

      // Epoch 1 is created by the server in DISTRIBUTING state; seal ONE key for
      // it to every initial member, so the key we hold IS epoch 1's key (no separate
      // flat key, no legacy /keys POST).
      const secretKey = useCryptoStore.getState().secretKey;
      const outcome = secretKey ? await bootstrapNewChannelKey(channel.id, secretKey) : null;
      if (outcome?.outcome !== 'ready') {
        console.warn('[E2EE] Initial channel key not distributed yet.', outcome);
        toast.error(
          'Group created, but encryption is still being set up. Messages can be sent once it finishes; reopen the group if it does not.',
        );
      }

      return { channel, addFailed };
    },
    onSuccess: ({ channel, addFailed }) => {
      queryClient.invalidateQueries({ queryKey: ['channels', orgId] });
      if (addFailed > 0) {
        toast.error(`Group created; could not add ${addFailed} member(s)`);
      }
      onClose();
      navigate(`/app/${orgSlug}/${channel.id}`);
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      toast.error(axiosErr.response?.data?.error?.message || 'Failed to create group');
    },
  });

  const toggleSelected = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const removeChip = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  return (
    <Modal open={open} onClose={onClose} title="New chat">
      <div className="mb-4 flex rounded-lg border border-gray-200 p-0.5 text-sm">
        <button
          type="button"
          onClick={() => setTab('dm')}
          className={`flex-1 rounded-md px-3 py-2 font-medium transition ${
            tab === 'dm' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          Direct message
        </button>
        <button
          type="button"
          onClick={() => setTab('group')}
          className={`flex-1 rounded-md px-3 py-2 font-medium transition ${
            tab === 'group' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          New group
        </button>
      </div>

      {tab === 'dm' && (
        <div className="space-y-3">
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="new-chat-dm-search"
            >
              Find teammate
            </label>
            <input
              id="new-chat-dm-search"
              type="search"
              value={dmQuery}
              onChange={(e) => setDmQuery(e.target.value)}
              placeholder="Name or email"
              autoComplete="off"
              className="w-full rounded border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div
            className="max-h-56 overflow-y-auto rounded border border-gray-100 bg-gray-50/80"
            role="listbox"
            aria-label="Organization members"
          >
            {membersError && <p className="p-3 text-sm text-red-600">Failed to load members</p>}
            {!membersError && membersLoading && (
              <p className="p-3 text-sm text-gray-500">Loading members…</p>
            )}
            {!membersError && !membersLoading && dmRows.length === 0 && (
              <p className="p-3 text-sm text-gray-500">
                {dmDebounced.trim() ? 'No matches' : 'No members'}
              </p>
            )}
            {!membersError &&
              !membersLoading &&
              dmRows.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  disabled={dmMutation.isPending}
                  onClick={() => dmMutation.mutate(m.id)}
                  className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-white disabled:opacity-50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-semibold text-primary">
                    {m.avatarUrl ? (
                      <img src={m.avatarUrl} alt="" className="h-8 w-8 rounded object-cover" />
                    ) : (
                      m.displayName[0]?.toUpperCase() || '?'
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-900">
                      {m.displayName}
                    </span>
                    <span className="block truncate text-xs text-gray-500">{m.email}</span>
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}

      {tab === 'group' && (
        <div className="space-y-4">
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="new-chat-gr-name"
            >
              Group name
            </label>
            <input
              id="new-chat-gr-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Morning Team"
              className="w-full rounded border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="new-chat-gr-search"
            >
              Add members (optional)
            </label>
            <input
              id="new-chat-gr-search"
              type="search"
              value={grQuery}
              onChange={(e) => setGrQuery(e.target.value)}
              placeholder="Name or email"
              autoComplete="off"
              className="w-full rounded border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
              Selected
            </p>
            <div className="flex min-h-[2rem] flex-wrap gap-1.5 rounded border border-dashed border-gray-200 bg-gray-50/80 px-2 py-2">
              {selectedIds.size === 0 ? (
                <span className="text-sm text-gray-400">None selected</span>
              ) : (
                Array.from(selectedIds).map((id) => {
                  const m = members.find((x) => x.id === id);
                  if (!m) return null;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => removeChip(id)}
                      className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
                      title="Remove"
                    >
                      {m.displayName} ×
                    </button>
                  );
                })
              )}
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto rounded border border-gray-100">
            {membersError && <p className="p-3 text-sm text-red-600">Failed to load members</p>}
            {!membersError && membersLoading && (
              <p className="p-3 text-sm text-gray-500">Loading members…</p>
            )}
            {!membersError &&
              !membersLoading &&
              grRows.map((m) => (
                <label
                  key={m.id}
                  className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={selectedIds.has(m.id)}
                    onChange={(e) => toggleSelected(m.id, e.target.checked)}
                  />
                  <span>
                    {m.displayName} <span className="text-gray-500">({m.email})</span>
                  </span>
                </label>
              ))}
          </div>
          <div className="flex justify-end pt-1">
            <button
              type="button"
              disabled={!groupName.trim() || createGroupMutation.isPending}
              onClick={() => createGroupMutation.mutate()}
              className="rounded bg-primary px-4 py-2 text-sm text-white hover:bg-primary-hover disabled:opacity-50"
            >
              {createGroupMutation.isPending ? 'Creating…' : 'Create group'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
